'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Time } from '@morpho-org/morpho-ts';
import type { OptionsStackingValue, SeriesOptionsType } from 'highcharts';
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs } from '@repo/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { type Address, formatUnits, zeroAddress } from 'viem';
import CumulativeChart from '@/components/charts/components/CumulativeChart';
import { colorAllocationSeries } from '@/components/charts/helpers/colorAllocationSeries';
import { type AllocationSeriesCustomData, formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config/table';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { unknownTokenLabel } from '@/lib/transforms/tokenLabel';
import type { AllocationAsset, AllocationCapTarget } from './allocationHistory.contract';
import { allocationHistoryQueryOptions, toHistoryRange } from './allocationHistory.query';
import { getCapSeriesName, getMarketSeriesName, marketCollateral } from './capSeriesName';
import { getChartedCapItems } from './chartedCapItems';
export interface ChartAllocationHistoryClientProps {
  vaultAddress: Address;
  chainId: ChainId;
}
const getStackingOptions = (symbol: string): {
  segmented: {
    value: OptionsStackingValue;
    label: string;
  }[];
  select: OptionSelect<OptionsStackingValue>[];
} => ({
  segmented: [{
    value: 'normal',
    label: symbol
  }, {
    value: 'percent',
    label: '%'
  }],
  select: [{
    value: 'normal',
    label: symbol
  }, {
    value: 'percent',
    label: '%'
  }]
});
const EMPTY_ASSET: AllocationAsset = {
  ...unknownTokenLabel(zeroAddress),
  priceUsd: null
};
const toAmount = (raw: string | null, decimals: number) => raw === null ? null : Number(formatUnits(BigInt(raw), decimals));
function getCapCustomData(target: AllocationCapTarget, assetLogoUri: string | null): AllocationSeriesCustomData {
  if (target.kind === 'market') {
    const collateral = marketCollateral(target);
    return {
      marketName: getMarketSeriesName(target, 10),
      formattedLltv: collateral !== null && target.lltv !== null ? formatters.percent.digits(2).unit('%').trailingZero(false).of(BigInt(target.lltv), 18) : null,
      loanAssetLogoURI: target.loanToken?.logoUri ?? null,
      collateralAssetLogoURI: collateral?.logoUri ?? null,
      isIdle: false
    };
  }
  return {
    marketName: getCapSeriesName(target),
    formattedLltv: null,
    // A vault adapter's target vault holds the same asset as this vault.
    loanAssetLogoURI: target.kind === 'vault' && target.vault !== null ? assetLogoUri : null,
    collateralAssetLogoURI: null,
    isIdle: false
  };
}

/**
 * The vault's allocation history per cap, plus its idle assets. The server hydrates the default
 * timeframe; another one keeps the previous range on screen in the chart's loading state until it
 * arrives, and a failed range shows the chart's error with the selectors still usable. Without the
 * asset decimals the amounts can't be read, so the chart shows its error too. A `partial` answer
 * still charts the caps it has, and marks the card with `data-partial`.
 */
const ChartAllocationHistoryClient = t0 => {
  const $ = _c(63);
  const {
    vaultAddress,
    chainId
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  const [stackingMode, setStackingMode] = useState("normal");
  let t1;
  if ($[0] !== chainId || $[1] !== timeFrame || $[2] !== vaultAddress) {
    t1 = allocationHistoryQueryOptions({
      chainId,
      address: vaultAddress,
      range: toHistoryRange(timeFrame)
    });
    $[0] = chainId;
    $[1] = timeFrame;
    $[2] = vaultAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      ...t1,
      placeholderData: keepPreviousData
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    isError,
    isPlaceholderData,
    isPending
  } = useQuery(t2);
  const loading = isPending || isPlaceholderData && !isError;
  const [lastAsset, setLastAsset] = useState(EMPTY_ASSET);
  if (data !== undefined && data.asset !== lastAsset) {
    setLastAsset(data.asset);
  }
  const asset = data?.asset ?? lastAsset;
  const symbol = asset.symbol ?? "";
  let T0;
  let decimals;
  let formattedData;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let unit;
  if ($[6] !== asset || $[7] !== data?.idle || $[8] !== data?.partial || $[9] !== data?.series || $[10] !== isMobile || $[11] !== palette || $[12] !== stackingMode || $[13] !== symbol) {
    const stackingOptions = getStackingOptions(symbol);
    unit = stackingMode === "percent" ? "%" : symbol;
    decimals = asset.decimals;
    let t12;
    if ($[27] !== asset || $[28] !== data?.idle || $[29] !== data?.series || $[30] !== decimals || $[31] !== palette) {
      const capSeries = decimals === null ? [] : getChartedCapItems({
        series: data?.series ?? [],
        asset
      }).map(item => ({
        type: "areaspline" as const,
        name: getCapSeriesName(item.target),
        data: formatTimeseriesData(item.points.map(t13 => {
          const {
            timestamp,
            allocation
          } = t13;
          return {
            x: timestamp,
            y: toAmount(allocation, decimals)
          };
        })),
        states: {
          inactive: {
            enabled: false
          }
        },
        lineWidth: 1,
        custom: getCapCustomData(item.target, asset.logoUri)
      }));
      const series = decimals === null ? [] : [...capSeries, {
        type: "areaspline" as const,
        name: "Idle",
        data: formatTimeseriesData((data?.idle ?? []).map(t14 => {
          const {
            timestamp: timestamp_0,
            idleAssets
          } = t14;
          return {
            x: timestamp_0,
            y: toAmount(idleAssets, decimals)
          };
        })),
        states: {
          inactive: {
            enabled: false
          }
        },
        lineWidth: 1,
        custom: {
          marketName: asset.symbol ?? "Idle",
          formattedLltv: null,
          loanAssetLogoURI: asset.logoUri,
          collateralAssetLogoURI: null,
          isIdle: true
        } satisfies AllocationSeriesCustomData
      }];
      t12 = colorAllocationSeries(series, palette);
      $[27] = asset;
      $[28] = data?.idle;
      $[29] = data?.series;
      $[30] = decimals;
      $[31] = palette;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    formattedData = t12;
    T0 = Card;
    t7 = "gap-sm w-full flex-col";
    t8 = "m";
    t9 = true;
    t10 = "vault-v2-allocation-history-chart";
    t11 = data?.partial ? "true" : undefined;
    t5 = "flex items-center justify-between";
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span className="text-secondary-foreground text-xs">Allocation History</span>;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    t3 = "gap-sm flex flex-wrap items-center";
    t4 = isMobile ? <Select options={stackingOptions.select} value={stackingMode} onChange={setStackingMode} /> : <Tabs value={stackingMode} onValueChange={value => setStackingMode(value as OptionsStackingValue)}><SegmentedControlList size="default" aria-label="Unit">{stackingOptions.segmented.map(_temp)}</SegmentedControlList></Tabs>;
    $[6] = asset;
    $[7] = data?.idle;
    $[8] = data?.partial;
    $[9] = data?.series;
    $[10] = isMobile;
    $[11] = palette;
    $[12] = stackingMode;
    $[13] = symbol;
    $[14] = T0;
    $[15] = decimals;
    $[16] = formattedData;
    $[17] = t10;
    $[18] = t11;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = unit;
  } else {
    T0 = $[14];
    decimals = $[15];
    formattedData = $[16];
    t10 = $[17];
    t11 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
    unit = $[26];
  }
  let t12;
  if ($[34] !== timeFrame) {
    t12 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} />;
    $[34] = timeFrame;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t3 || $[38] !== t4) {
    t13 = <div className={t3}>{t4}{t12}</div>;
    $[36] = t12;
    $[37] = t3;
    $[38] = t4;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t5 || $[42] !== t6) {
    t14 = <div className={t5}>{t6}{t13}</div>;
    $[40] = t13;
    $[41] = t5;
    $[42] = t6;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const t15 = isError || data !== undefined && decimals === null;
  let t16;
  if ($[44] !== loading || $[45] !== t15) {
    t16 = {
      loading,
      error: t15
    };
    $[44] = loading;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const t17 = asset.symbol ?? undefined;
  let t18;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== formattedData || $[49] !== stackingMode || $[50] !== t16 || $[51] !== t17 || $[52] !== unit) {
    t19 = <CumulativeChart statuses={t16} unit={unit} stackingMode={stackingMode} tooltipMode="allocation" assetSymbol={t17} height="300px" series={formattedData} additionalOptions={t18} />;
    $[48] = formattedData;
    $[49] = stackingMode;
    $[50] = t16;
    $[51] = t17;
    $[52] = unit;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== T0 || $[55] !== t10 || $[56] !== t11 || $[57] !== t14 || $[58] !== t19 || $[59] !== t7 || $[60] !== t8 || $[61] !== t9) {
    t20 = <T0 className={t7} padding={t8} withBorder={t9} data-testid={t10} data-partial={t11}>{t14}{t19}</T0>;
    $[54] = T0;
    $[55] = t10;
    $[56] = t11;
    $[57] = t14;
    $[58] = t19;
    $[59] = t7;
    $[60] = t8;
    $[61] = t9;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  return t20;
};
export default ChartAllocationHistoryClient;
function _temp(option) {
  return <SegmentedControlItem key={option.value} value={option.value}>{option.label}</SegmentedControlItem>;
}